import { Box, useTheme } from '@mui/material'
import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import ReactLoader from 'react-loader'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { RootState } from '@/store'
import { useUploadCaseImagesMutation } from '@/store/apiService'
import { getCaseId } from '@/utils'

import ImageProgressBar from '../../ImageProgressBar'

interface PresignedUrlResponse {
  url: string
  key: string
}

interface FileWithPresignedData {
  presignedData: PresignedUrlResponse
  file: File
}

interface ImageUploadProps {
  file: FileWithPresignedData | any
  setPollingInterval: React.Dispatch<React.SetStateAction<number>>
}

const ImageSkeleton: React.FC<ImageUploadProps> = ({
  file,
  setPollingInterval,
}) => {
  const theme = useTheme()
  const caseId = getCaseId()
  const [uploadCaseImages] = useUploadCaseImagesMutation()
  const [progress, setProgress] = useState(0)
  const uploadInitiatedRef = useRef(false)
  const previousFileRef = useRef<File | null>(null)
  const isImageUploadFailed = useSelector(
    (state: RootState) => state.app.isImageUploadFailed,
  )

  useEffect(() => {
    if (isImageUploadFailed) {
      setPollingInterval(0)
    }
  }, [isImageUploadFailed])

  useEffect(() => {
    if (previousFileRef.current !== file.file) {
      uploadInitiatedRef.current = false
      previousFileRef.current = file.file
      setProgress(0)
    }
  }, [file.file])

  useEffect(() => {
    const handleImageUpload = async () => {
      if (uploadInitiatedRef.current) {
        return
      }

      if (!(file.file instanceof File)) {
        return
      }

      if (!file?.presignedData?.url || !file?.presignedData?.key) {
        return
      }

      uploadInitiatedRef.current = true

      try {
        await axios.put(file?.presignedData?.url, file.file, {
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              )
              setProgress(percentCompleted)
            }
          },
        })

        toast.info('Processing started.')

        await uploadCaseImages({
          caseId: caseId || '',
          key: file?.presignedData?.key,
        }).unwrap()
        setPollingInterval(5000)
      } catch (error) {
        setProgress(0)
        if (error instanceof Error) {
          toast.error(`Upload failed: ${error.message}`)
        } else {
          toast.error('An unexpected error occurred during upload')
        }
      }
    }

    handleImageUpload()
  }, [
    file.file,
    file?.presignedData?.key,
    file?.presignedData?.url,
    caseId,
    uploadCaseImages,
    setPollingInterval,
  ])

  if (progress === 0) {
    return null
  }

  return (
    <Box pb={1}>
      <Box
        bgcolor={`${theme.palette.grey[50]}10`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="12rem"
        width="100%"
        position="relative"
        p={2}
        borderRadius={1}
        border={`1px solid ${theme.palette.divider}`}
      >
        {progress >= 100 ? (
          <ReactLoader
            loaded={false}
            lines={20}
            length={20}
            width={3}
            radius={25}
            color={theme.palette.text.primary}
          />
        ) : (
          <ImageProgressBar value={progress} />
        )}
      </Box>
    </Box>
  )
}

export default ImageSkeleton
